import React, { useState, useEffect, useMemo, useCallback } from "react";
import Grid from "./Grid";
import ModuleRenderer from "../renderers/ModuleRenderer";
import AddModuleButtonRenderer from "../renderers/AddModuleButtonRenderer";
import DockingJointRenderer from "../renderers/dockingUtils";
import FramelessWallRenderer from "../renderers/FramelessWallRenderer";
import FrameRenderer from "./FrameRenderer";
import FramePlacementSlotRenderer from "../renderers/FramePlacementSlotRenderer";
import PlacementSlotRenderer from "../renderers/PlacementSlotRenderer";
import DrawingNodes from "./DrawingNodes";
import { MODES, OBJECT_TYPES, INITIAL_PPM, GRID_CELL_SIZE_M, FRAMELESS_GRID_CELL_SIZE_M, POTENTIAL_WALL_SLOT_COLOR, SELECTED_ELEMENT_COLOR } from "../configuratorConstants";
import useCanvasInteractions from '../hooks/useCanvasInteractions';
import { calculateFramelessPartitionSegments } from "../utils/framelessPartitionUtils";
import { useDraggable } from '@dnd-kit/core';


const isJointSuitableForPlacement = (joint, elementType) => {
  if (!elementType || !joint) return false;

  if (elementType === OBJECT_TYPES.INTERNAL_WALL_TOOL) {
    return joint.displayMode === 'passage';
  }

  if (joint.displayMode === 'passage') return false;

  const numSlots = Math.max(1, Math.round(joint.width / GRID_CELL_SIZE_M));

  const passageSegments = joint.passageSegments || [];

  if (elementType === OBJECT_TYPES.DOOR) {
    const occupiedSlots = new Set();
    const doorSlots = new Set();

    joint.elements?.forEach(exEl => {
      const exElSlotIndex = Math.max(0, Math.min(numSlots - 1, Math.floor(exEl.positionOnSegment * numSlots)));
      occupiedSlots.add(exElSlotIndex);
      if (exEl.type === OBJECT_TYPES.DOOR) {
        doorSlots.add(exElSlotIndex);
      }
    });

    for (let i = 0; i < numSlots; i++) {
      if (passageSegments.includes(i)) continue;
      if (occupiedSlots.has(i)) continue;

      const isAdjacentToDoor = doorSlots.has(i - 1) || doorSlots.has(i + 1);
      if (!isAdjacentToDoor) {
        return true;
      }
    }

    return false;
  } else {
    const occupiedSlots = new Set();
    const isPanoramicWindow = elementType === OBJECT_TYPES.PANORAMIC_WINDOW;
    const slotsNeeded = isPanoramicWindow ? 2 : 1;

    joint.elements?.forEach(exEl => {
      const slotIndex = Math.floor((exEl.positionOnSegment || 0.5) * numSlots);
      const clampedIndex = Math.max(0, Math.min(numSlots - 1, isNaN(slotIndex) ? 0 : slotIndex));
      occupiedSlots.add(clampedIndex);

      // Если существующий элемент - панорамное окно, оно занимает 2 слота
      if (exEl.type === OBJECT_TYPES.PANORAMIC_WINDOW && clampedIndex + 1 < numSlots) {
        occupiedSlots.add(clampedIndex + 1);
      }
    });

    // Проверяем, есть ли место для размещения элемента
    if (isPanoramicWindow) {
      // Для панорамного окна нужно 2 свободных соседних слота
      for (let i = 0; i <= numSlots - slotsNeeded; i++) {
        if (passageSegments.includes(i) || passageSegments.includes(i + 1)) continue;
        if (!occupiedSlots.has(i) && !occupiedSlots.has(i + 1)) {
          return true;
        }
      }
      return false;
    } else {
      const occupiedCount = occupiedSlots.size;
      const blockedByPassages = (passageSegments || []).length;
      const availableSlots = Math.max(0, numSlots - occupiedCount - blockedByPassages);
      return availableSlots > 0;
    }
  }
};

const isFramelessWallSuitable = (wall, elementType) => {
  if (!wall) return false;
  // Не подсвечиваем стены для INTERNAL_WALL_TOOL - внутренние стены размещаются внутри комнаты
  if (elementType === OBJECT_TYPES.INTERNAL_WALL_TOOL) return false;
  const isSupportedWall =
    wall.type === OBJECT_TYPES.FRAMELESS_WALL ||
    wall.type === OBJECT_TYPES.FRAMELESS_PARTITION;
  return (
    isSupportedWall &&
    elementType &&
    elementType !== OBJECT_TYPES.FRAMELESS_WALL_TOOL
  );
};

const getSuitableWindowsForBlinds = (objects, elementType) => {
  if (elementType !== OBJECT_TYPES.WINDOW_BLINDS) {
    return [];
  }

  const suitableWindows = [];

  objects.forEach(obj => {
    // Обработка модульного режима
    if (obj.type === OBJECT_TYPES.MODULE && obj.wallSegments) {
      Object.values(obj.wallSegments).forEach(wallSegment => {
        if (wallSegment.elements) {
          wallSegment.elements.forEach(element => {
            if (element.type === OBJECT_TYPES.WINDOW || element.type === OBJECT_TYPES.PANORAMIC_WINDOW) {
              const hasBlinds = wallSegment.elements.some(el =>
                el.type === OBJECT_TYPES.WINDOW_BLINDS &&
                Math.abs(el.positionOnSegment - element.positionOnSegment) < 0.1
              );

              if (!hasBlinds) {
                suitableWindows.push({
                  windowElement: element,
                  wallSegment: wallSegment,
                  module: obj,
                  wallSegmentId: wallSegment.id
                });
              }
            }
          });
        }
      });
    }

    // Обработка безкаркасного режима
    if (obj.type === OBJECT_TYPES.FRAMELESS_WALL || obj.type === OBJECT_TYPES.FRAMELESS_PARTITION) {
      if (obj.segments && Array.isArray(obj.segments)) {
        obj.segments.forEach(segment => {
          if (segment.elements && Array.isArray(segment.elements)) {
            segment.elements.forEach(element => {
              if (element.type === OBJECT_TYPES.WINDOW || element.type === OBJECT_TYPES.PANORAMIC_WINDOW) {
                const hasBlinds = segment.elements.some(el =>
                  el.type === OBJECT_TYPES.WINDOW_BLINDS &&
                  Math.abs((el.positionOnSegment || 0.5) - (element.positionOnSegment || 0.5)) < 0.1
                );

                if (!hasBlinds) {
                  suitableWindows.push({
                    windowElement: element,
                    wallSegment: segment,
                    wall: obj,
                    wallSegmentId: segment.id
                  });
                }
              }
            });
          }
        });
      }
    }

    // Обработка безкаркасных зданий
    if (obj.type === OBJECT_TYPES.FRAMELESS_BUILDING) {
      const allWalls = [
        ...(obj.perimeterWalls || []),
        ...(obj.internalWalls || [])
      ];

      allWalls.forEach(wall => {
        if (wall.segments && Array.isArray(wall.segments)) {
          wall.segments.forEach(segment => {
            if (segment.elements && Array.isArray(segment.elements)) {
              segment.elements.forEach(element => {
                if (element.type === OBJECT_TYPES.WINDOW || element.type === OBJECT_TYPES.PANORAMIC_WINDOW) {
                  const hasBlinds = segment.elements.some(el =>
                    el.type === OBJECT_TYPES.WINDOW_BLINDS &&
                    Math.abs((el.positionOnSegment || 0.5) - (element.positionOnSegment || 0.5)) < 0.1
                  );

                  if (!hasBlinds) {
                    suitableWindows.push({
                      windowElement: element,
                      wallSegment: segment,
                      wall: wall,
                      building: obj,
                      wallSegmentId: segment.id
                    });
                  }
                }
              });
            }
          });
        }
      });
    }
  });

  return suitableWindows;
};

const SvgCanvas = ({
  svgRef,
  viewTransform,
  setViewTransform,
  screenToWorld,
  mainContainerRef,
  updateModulePosition,
  snapAndFinalizeModulePosition,
  onContextMenu,
  objects,
  ghostObjects,
  activeMode,
  selectedObjectId,
  setSelectedObjectId,
  canAddInitialModule,
  onAddModule,
  initialModulePlacementCoords,
  onToggleWallSegment,
  primarySelectedObject,
  elementTypeToPlace,
  onWallSegmentClick,
  isPlacementModeActive,
  placementSlots,
  onPlacementSlotClick,
  currentFloorIndex,
  allJoints,
  drawingWall,
  dispatch,
  children,
  moduleToMove,
  ghostModulePosition,
  savedModuleForMove,
  internalWallSlots = [],
  onInternalWallSlotClick,
  framelessLastWallId,
  activeDragFrame,
}) => {
  const [svgDimensions, setSvgDimensions] = useState({ width: 0, height: 0 });

  useEffect(() => {
    const currentSvg = svgRef.current;
    if (!currentSvg) return;
    const resizeObserver = new ResizeObserver((entries) => {
      for (let entry of entries) {
        const { width, height } = entry.contentRect;
        setSvgDimensions({ width, height });
      }
    });
    resizeObserver.observe(currentSvg);
    const rect = currentSvg.getBoundingClientRect();
    if (rect.width > 0 && rect.height > 0) {
      setSvgDimensions({ width: rect.width, height: rect.height });
    }
    return () => {
      if (currentSvg) resizeObserver.unobserve(currentSvg);
    };
  }, [svgRef]);


  useCanvasInteractions({
    viewTransform,
    setViewTransform,
    svgRef,
    mainContainerRef,
    activeMode,
    setSelectedObjectId,
    screenToWorld,
    updateModulePosition,
    snapAndFinalizeModulePosition,
    onWallSegmentClick,
    elementTypeToPlace,
    onContextMenu,
    dispatch,
  });

  let cursorClass = "cursor-default";
  if (elementTypeToPlace === OBJECT_TYPES.FRAMELESS_WALL_TOOL) {
    cursorClass = "cursor-crosshair";
  } else if (elementTypeToPlace) {
    cursorClass = "cursor-copy";
  } else if (isPlacementModeActive) {
    cursorClass = "cursor-pointer";
  }


  const existingModulesCount = objects.filter(
    (obj) => obj.type === OBJECT_TYPES.MODULE
  ).length;

  const currentGridSize = activeMode === MODES.FRAMELESS ? FRAMELESS_GRID_CELL_SIZE_M : GRID_CELL_SIZE_M;

  const renderDrawingWall = () => {
    if (!drawingWall) return null;
    const { startPoint, endPoint, isExceeded } = drawingWall;

    const dx = Math.abs(endPoint.x - startPoint.x);
    const dy = Math.abs(endPoint.y - startPoint.y);
    const epsilon = 0.01;
    const isHorizontal = dy < epsilon;
    const isVertical = dx < epsilon;

    if (!isHorizontal && !isVertical) {
      return null;
    }

    const lineColor = isExceeded ? "#FF0000" : "#C71D3B";

    return (
      <>
        <line
          x1={startPoint.x * viewTransform.scale}
          y1={startPoint.y * viewTransform.scale}
          x2={endPoint.x * viewTransform.scale}
          y2={endPoint.y * viewTransform.scale}
          stroke={lineColor}
          strokeWidth="3"
          strokeDasharray="8, 4"
          pointerEvents="none"
          opacity="0.8"
        />
        <circle
          cx={startPoint.x * viewTransform.scale}
          cy={startPoint.y * viewTransform.scale}
          r="6"
          fill={lineColor}
          stroke="white"
          strokeWidth="2"
          pointerEvents="none"
        />
        <circle
          cx={endPoint.x * viewTransform.scale}
          cy={endPoint.y * viewTransform.scale}
          r="6"
          fill={lineColor}
          stroke="white"
          strokeWidth="2"
          pointerEvents="none"
        />
      </>
    );
  };

  return (
    <svg
      ref={svgRef}
      width="100%"
      height="100%"
      className={`block ${cursorClass} bg-white touch-none`}
    >
      {svgDimensions.width > 0 && svgDimensions.height > 0 && (
        <>
          <Grid
            viewTransform={viewTransform}
            svgWidth={svgDimensions.width}
            svgHeight={svgDimensions.height}
            cellSize={currentGridSize}
          />
          {activeMode === MODES.FRAMELESS && elementTypeToPlace === OBJECT_TYPES.FRAMELESS_WALL_TOOL && (
            <DrawingNodes
              viewTransform={viewTransform}
              svgWidth={svgDimensions.width}
              svgHeight={svgDimensions.height}
              framelessLastWallId={framelessLastWallId}
              objects={objects}
            />
          )}
        </>
      )}
      <g
        transform={`translate(${viewTransform.x}, ${viewTransform.y})`}
      >
        <g id="ghosts" opacity="0.3" style={{ pointerEvents: 'none' }}>
          {ghostObjects && ghostObjects.map((obj) => {
            if (obj.type === OBJECT_TYPES.MODULE) {
              return (
                <ModuleRenderer
                  key={`ghost-${obj.id}`}
                  module={obj}
                  scale={viewTransform.scale}
                  isGhost={true}
                  selectedObjectId={null}
                  setSelectedObjectId={() => { }}
                  onContextMenu={() => { }}
                  currentFloorIndex={currentFloorIndex - 1}
                />
              );
            }
            return null;
          })}
        </g>

        <g id="placement-slots">
          {isPlacementModeActive && placementSlots.map(slot => {
            // Для каркасов используем FramePlacementSlotRenderer
            if (activeDragFrame && activeMode === MODES.FRAME) {
              return (
                <FramePlacementSlotRenderer
                  key={`${slot.x}-${slot.y}-${slot.rotation}`}
                  slot={slot}
                  scale={viewTransform.scale}
                  onClick={() => onPlacementSlotClick && onPlacementSlotClick(slot)}
                />
              );
            }
            // Для модулей используем обычный PlacementSlotRenderer
            return (
              <PlacementSlotRenderer
                key={`${slot.x}-${slot.y}-${slot.rotation}`}
                slot={slot}
                scale={viewTransform.scale}
                onClick={() => onPlacementSlotClick(slot)}
                ghostModulePosition={ghostModulePosition}
                savedModuleForMove={savedModuleForMove}
                moduleToMove={moduleToMove}
              />
            );
          })}
        </g>

        {/* Слоты для внутренних стен внутри комнат - линии сетки */}
        {activeMode === MODES.FRAMELESS && elementTypeToPlace === OBJECT_TYPES.INTERNAL_WALL_TOOL && internalWallSlots.length > 0 && (
          <g id="internal-wall-slots">
            {internalWallSlots.map(slot => {
              const startX = slot.startPoint.x * viewTransform.scale;
              const startY = slot.startPoint.y * viewTransform.scale;
              const endX = slot.endPoint.x * viewTransform.scale;
              const endY = slot.endPoint.y * viewTransform.scale;

              return (
                <g key={slot.id}>
                  {/* Невидимая линия с большой областью клика */}
                  <line
                    x1={startX}
                    y1={startY}
                    x2={endX}
                    y2={endY}
                    stroke="transparent"
                    strokeWidth={30}
                    style={{ cursor: 'pointer' }}
                    onClick={(e) => {
                      e.stopPropagation();
                      if (onInternalWallSlotClick) {
                        onInternalWallSlotClick(slot);
                      }
                    }}
                  />
                  {/* Визуальная линия */}
                  <line
                    x1={startX}
                    y1={startY}
                    x2={endX}
                    y2={endY}
                    stroke={POTENTIAL_WALL_SLOT_COLOR}
                    strokeWidth={6}
                    strokeDasharray="4,4"
                    opacity={0.8}
                    style={{ pointerEvents: 'none' }}
                  />
                </g>
              );
            })}
          </g>
        )}

        {objects.map((obj) => {
          if (obj.type === OBJECT_TYPES.MODULE && activeMode === MODES.MODULAR) {
            return (
              <ModuleRenderer
                key={obj.id}
                module={obj}
                scale={viewTransform.scale}
                selectedObjectId={selectedObjectId}
                setSelectedObjectId={setSelectedObjectId}
                onToggleWallSegment={onToggleWallSegment}
                primarySelectedObject={primarySelectedObject}
                onContextMenu={onContextMenu}
                elementTypeToPlace={elementTypeToPlace}
                onWallSegmentClick={onWallSegmentClick}
                currentFloorIndex={currentFloorIndex}
                isGhost={false}
              />
            );
          }
          if (obj.type === OBJECT_TYPES.FRAMELESS_WALL && activeMode === MODES.FRAMELESS) {
            let selectedSegmentId = null;
            let selectedElementIdOnThisWall = null;

            if (primarySelectedObject) {
              if (primarySelectedObject.parentWallId === obj.id && primarySelectedObject.type === 'frameless_wall_segment') {
                selectedSegmentId = primarySelectedObject.id;
              }
              if (primarySelectedObject.parentWallId === obj.id && primarySelectedObject.type !== 'frameless_wall_segment') {
                selectedElementIdOnThisWall = primarySelectedObject.id;
              }
            }

            return (
              <FramelessWallRenderer
                key={obj.id}
                wall={obj}
                scale={viewTransform.scale}
                isSelected={selectedSegmentId !== null}
                selectedElementId={selectedElementIdOnThisWall || selectedSegmentId}
                onSelectWall={setSelectedObjectId}
                onSelectElement={setSelectedObjectId}
                onContextMenu={onContextMenu}
                isInPlacementMode={!!elementTypeToPlace && elementTypeToPlace !== OBJECT_TYPES.FRAMELESS_WALL_TOOL && elementTypeToPlace !== OBJECT_TYPES.INTERNAL_WALL_TOOL}
                highlightForPlacement={isFramelessWallSuitable(obj, elementTypeToPlace)}
                onWallSegmentClick={onWallSegmentClick}
                screenToWorld={screenToWorld}
                elementTypeToPlace={elementTypeToPlace}
              />
            )
          }
          if (obj.type === OBJECT_TYPES.FRAME_STRUCTURE && activeMode === MODES.FRAME) {
            console.log('🖼️ [SvgCanvas] Rendering FRAME_STRUCTURE object:', {
              id: obj.id,
              x: obj.x,
              y: obj.y,
              width: obj.width,
              length: obj.length,
              gridWidth: obj.gridWidth,
              gridLength: obj.gridLength,
              hasPerimeterWalls: !!obj.perimeterWalls && Object.keys(obj.perimeterWalls).length > 0,
              perimeterWallsCount: obj.perimeterWalls ? Object.keys(obj.perimeterWalls).length : 0,
              scale: viewTransform.scale,
              objKeys: Object.keys(obj),
              viewTransform,
            });
            try {
              return (
                <FrameRenderer
                  key={obj.id}
                  frame={obj}
                  scale={viewTransform.scale}
                  isSelected={selectedObjectId === obj.id}
                  onClick={(id) => setSelectedObjectId(id)}
                />
              );
            } catch (error) {
              console.error('❌ [SvgCanvas] Error rendering FrameRenderer:', error, { obj, scale: viewTransform.scale });
              return null;
            }
          }
          if (obj.type === OBJECT_TYPES.DOCKING_JOINT && activeMode === MODES.MODULAR) {
            const shouldHighlightJoint = isJointSuitableForPlacement(obj, elementTypeToPlace);
            return (
              <DockingJointRenderer
                key={obj.id}
                joint={obj}
                allJoints={allJoints}
                allModules={objects.filter(obj => obj.type === OBJECT_TYPES.MODULE)}
                scale={viewTransform.scale}
                isSelected={selectedObjectId === obj.id}
                selectedElementId={primarySelectedObject?.id && obj.elements?.some(e => e.id === primarySelectedObject.id) ? primarySelectedObject.id : null}
                onSelectJoint={setSelectedObjectId}
                onSelectElement={setSelectedObjectId}
                onContextMenu={onContextMenu}
                isInPlacementMode={!!elementTypeToPlace}
                highlightForPlacement={shouldHighlightJoint}
              />
            )
          }
          if (obj.type === OBJECT_TYPES.FRAMELESS_PARTITION && activeMode === MODES.FRAMELESS) {
            let selectedSegmentId = null;
            let selectedElementIdOnPartition = null;

            if (primarySelectedObject) {
              const belongsToPartition =
                primarySelectedObject.parentWallId === obj.id ||
                primarySelectedObject.parentPartitionId === obj.id;

              if (belongsToPartition && primarySelectedObject.type === 'frameless_wall_segment') {
                selectedSegmentId = primarySelectedObject.id;
              }

              if (belongsToPartition && primarySelectedObject.type !== 'frameless_wall_segment') {
                selectedElementIdOnPartition = primarySelectedObject.id;
              }
            }

            const hasSegments = Array.isArray(obj.segments) && obj.segments.length > 0;
            let partitionToRender = obj;

            if (!hasSegments) {
              partitionToRender = {
                ...obj,
                segments: calculateFramelessPartitionSegments(obj),
              };
            }

            return (
              <FramelessWallRenderer
                key={obj.id}
                wall={partitionToRender}
                scale={viewTransform.scale}
                isSelected={selectedSegmentId !== null}
                selectedElementId={selectedElementIdOnPartition || selectedSegmentId}
                onSelectWall={setSelectedObjectId}
                onSelectElement={setSelectedObjectId}
                onContextMenu={onContextMenu}
                isInPlacementMode={!!elementTypeToPlace && elementTypeToPlace !== OBJECT_TYPES.FRAMELESS_WALL_TOOL && elementTypeToPlace !== OBJECT_TYPES.INTERNAL_WALL_TOOL}
                highlightForPlacement={isFramelessWallSuitable(obj, elementTypeToPlace)}
                onWallSegmentClick={onWallSegmentClick}
                objectType={OBJECT_TYPES.FRAMELESS_PARTITION}
                screenToWorld={screenToWorld}
                elementTypeToPlace={elementTypeToPlace}
              />
            );
          }
          return null;
        })}
        {renderDrawingWall()}
        {children}

        {/* Ghost модуль, который следует за курсором при перемещении - рендерится последним */}
        {moduleToMove && ghostModulePosition && savedModuleForMove && (
          <g id="moving-ghost-module" opacity="0.5" style={{ pointerEvents: 'none' }}>
            <ModuleRenderer
              key={`moving-ghost-${savedModuleForMove.id}`}
              module={{
                ...savedModuleForMove,
                x: ghostModulePosition.x,
                y: ghostModulePosition.y,
              }}
              scale={viewTransform.scale}
              isGhost={true}
              selectedObjectId={null}
              setSelectedObjectId={() => { }}
              onContextMenu={() => { }}
              currentFloorIndex={currentFloorIndex}
            />
          </g>
        )}

        {/* Ghost каркас при перетаскивании */}
        {activeDragFrame && ghostModulePosition && (() => {
          const gridWidth = activeDragFrame.sandwichCountWidth || 1;
          const gridLength = activeDragFrame.sandwichCountLength || 1;
          // Создаем perimeterWalls для ghost frame
          const ghostPerimeterWalls = {};
          for (let i = 0; i < gridWidth; i++) {
            ghostPerimeterWalls[`${i},0_h`] = { id: `ghost-wall-${i}-0-h`, elements: [] };
            ghostPerimeterWalls[`${i},${gridLength}_h`] = { id: `ghost-wall-${i}-${gridLength}-h`, elements: [] };
          }
          for (let i = 0; i < gridLength; i++) {
            ghostPerimeterWalls[`0,${i}_v`] = { id: `ghost-wall-0-${i}-v`, elements: [] };
            ghostPerimeterWalls[`${gridWidth},${i}_v`] = { id: `ghost-wall-${gridWidth}-${i}-v`, elements: [] };
          }
          return (
            <FrameRenderer
              key="ghost-frame-preview"
              frame={{
                ...activeDragFrame,
                id: 'ghost-frame-preview',
                x: ghostModulePosition.x || 0,
                y: ghostModulePosition.y || 0,
                width: gridWidth * FRAMELESS_GRID_CELL_SIZE_M,
                length: gridLength * FRAMELESS_GRID_CELL_SIZE_M,
                gridWidth,
                gridLength,
                rotation: 0,
                perimeterWalls: ghostPerimeterWalls,
                internalPartitions: {},
              }}
              scale={viewTransform.scale}
              selectedObjectId={null}
              setSelectedObjectId={() => { }}
              primarySelectedObject={null}
              onContextMenu={() => { }}
              elementTypeToPlace={null}
              onWallSegmentClick={() => { }}
              dispatch={() => { }}
              style={{ opacity: 0.5, pointerEvents: 'none' }}
            />
          );
        })()}

      </g>
    </svg>
  );
};

export default SvgCanvas;